import {
  Component, OnInit, AfterViewInit,
  ViewChild, ElementRef, Input, NgZone, ChangeDetectorRef
} from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';

type DrawTool = 'pen' | 'arrow' | 'circle' | 'rect' | 'text';

interface DrawAction {
  tool: DrawTool;
  color: string;
  lineWidth: number;
  points?: Array<{ x: number; y: number }>;
  start?: { x: number; y: number };
  end?: { x: number; y: number };
  text?: string;
}

@Component({
  selector: 'app-image-annotator-modal',
  templateUrl: './image-annotator-modal.component.html',
  styleUrls: ['./image-annotator-modal.component.scss']
})
export class ImageAnnotatorModalComponent implements OnInit, AfterViewInit {
  @Input() imageUrl: string;
  @ViewChild('annotatorCanvas') canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasWrapper') wrapperRef: ElementRef<HTMLDivElement>;
  @ViewChild('canvasInner') innerRef: ElementRef<HTMLDivElement>;

  currentTool: DrawTool = 'pen';
  currentColor = '#FF0000';
  currentSize = 4;
  readonly colors = ['#FF0000', '#FFFF00', '#00BFFF', '#00CC00', '#000000', '#FFFFFF'];

  zoomLevel = 1;
  panX = 0;
  panY = 0;

  private ctx: CanvasRenderingContext2D;
  private bgImage: HTMLImageElement;
  private actions: DrawAction[] = [];
  private isDrawing = false;
  private startPoint: { x: number; y: number } = { x: 0, y: 0 };
  private currentPoints: Array<{ x: number; y: number }> = [];

  private isPinching = false;
  private lastPinchDist = 0;
  private lastPinchMid = { x: 0, y: 0 };

  constructor(
    private modalController: ModalController,
    private alertController: AlertController,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {}

  ngAfterViewInit() {
    const wrapper = this.wrapperRef.nativeElement;

    // Walk ALL light-DOM ancestors and force touch-action: none.
    // This prevents ion-modal's wrapper divs from intercepting multi-touch.
    let el: Element = wrapper;
    while (el && el.tagName.toLowerCase() !== 'html') {
      (el as HTMLElement).style.touchAction = 'none';
      (el as HTMLElement).style.webkitUserSelect = 'none';
      el = el.parentElement;
    }

    // ── Touch events on the canvas-area wrapper ──────────────────────────────
    wrapper.addEventListener('touchstart', (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length >= 2) {
        if (this.isDrawing) { this.isDrawing = false; this.redrawAll(); }
        this.startPinch(e);
        return;
      }
      if (this.isPinching) return;
      const t = e.touches[0];
      this.beginDraw(this.getCanvasPoint(t.clientX, t.clientY));
    }, { passive: false });

    wrapper.addEventListener('touchmove', (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length >= 2 && this.isPinching) {
        this.movePinch(e);
        return;
      }
      if (this.isPinching || !this.isDrawing || e.touches.length !== 1) return;
      const t = e.touches[0];
      this.moveDraw(this.getCanvasPoint(t.clientX, t.clientY));
    }, { passive: false });

    wrapper.addEventListener('touchend', (e: TouchEvent) => {
      e.preventDefault();
      if (this.isPinching) {
        if (e.touches.length < 2) {
          this.isPinching = false;
          // Notify Angular CD so zoomPercent / *ngIf update
          this.zone.run(() => { this.cdr.detectChanges(); });
        }
        return;
      }
      if (this.isDrawing && e.changedTouches.length > 0) {
        const t = e.changedTouches[0];
        this.finishDraw(this.getCanvasPoint(t.clientX, t.clientY));
      }
      this.isDrawing = false;
    }, { passive: false });

    wrapper.addEventListener('touchcancel', () => {
      this.isPinching = false;
      if (this.isDrawing) { this.isDrawing = false; this.redrawAll(); }
    }, { passive: false });

    // ── Mouse wheel zoom (desktop) ────────────────────────────────────────────
    wrapper.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 0.89;
      this.zoomAtPoint(e.clientX, e.clientY, this.zoomLevel * factor);
      this.applyTransform();
      this.zone.run(() => { this.cdr.detectChanges(); });
    }, { passive: false });

    this.initCanvas();
  }

  // ── Direct DOM transform (bypasses Angular CD for smooth 60 fps) ──────────

  private applyTransform() {
    if (this.innerRef) {
      this.innerRef.nativeElement.style.transform =
        `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`;
    }
  }

  // ── Canvas init ───────────────────────────────────────────────────────────

  private initCanvas() {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d');

    this.bgImage = new Image();
    this.bgImage.onload = () => {
      const toolbarH = 100;
      const actionBarH = 64;
      const maxW = window.innerWidth;
      const maxH = window.innerHeight - toolbarH - actionBarH - 16;
      const scale = Math.min(maxW / this.bgImage.naturalWidth, maxH / this.bgImage.naturalHeight, 1);
      canvas.width = Math.floor(this.bgImage.naturalWidth * scale);
      canvas.height = Math.floor(this.bgImage.naturalHeight * scale);
      this.panX = 0;
      this.panY = 0;
      this.zoomLevel = 1;
      this.applyTransform();
      this.redrawAll();
    };
    this.bgImage.src = this.imageUrl;
  }

  // ── Pinch helpers ─────────────────────────────────────────────────────────

  private touchDist(t0: Touch, t1: Touch): number {
    const dx = t1.clientX - t0.clientX;
    const dy = t1.clientY - t0.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private touchMid(t0: Touch, t1: Touch): { x: number; y: number } {
    return { x: (t0.clientX + t1.clientX) / 2, y: (t0.clientY + t1.clientY) / 2 };
  }

  private startPinch(e: TouchEvent) {
    this.isPinching = true;
    this.lastPinchDist = this.touchDist(e.touches[0], e.touches[1]);
    this.lastPinchMid  = this.touchMid(e.touches[0], e.touches[1]);
  }

  private movePinch(e: TouchEvent) {
    const t0 = e.touches[0];
    const t1 = e.touches[1];
    const currentDist = this.touchDist(t0, t1);
    const currentMid  = this.touchMid(t0, t1);

    if (this.lastPinchDist < 1) {
      this.lastPinchDist = currentDist;
      this.lastPinchMid  = currentMid;
      return;
    }

    const scaleDelta = currentDist / this.lastPinchDist;
    this.zoomAtPoint(this.lastPinchMid.x, this.lastPinchMid.y, this.zoomLevel * scaleDelta);
    this.panX += currentMid.x - this.lastPinchMid.x;
    this.panY += currentMid.y - this.lastPinchMid.y;

    // Direct DOM update — no Angular CD needed for the transform itself
    this.applyTransform();
    // Keep template bindings (zoomPercent, *ngIf) in sync at low cost
    this.zone.run(() => {});

    this.lastPinchDist = currentDist;
    this.lastPinchMid  = currentMid;
  }

  // ── Drawing ───────────────────────────────────────────────────────────────

  private redrawAll() {
    const c = this.canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, c.width, c.height);
    if (this.bgImage && this.bgImage.complete && this.bgImage.naturalWidth > 0) {
      this.ctx.drawImage(this.bgImage, 0, 0, c.width, c.height);
    }
    this.actions.forEach(a => this.drawAction(a));
  }

  private drawAction(action: DrawAction) {
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = action.color;
    ctx.fillStyle = action.color;
    ctx.lineWidth = action.lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (action.tool) {
      case 'pen':
        if (action.points && action.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(action.points[0].x, action.points[0].y);
          for (let i = 1; i < action.points.length; i++) {
            ctx.lineTo(action.points[i].x, action.points[i].y);
          }
          ctx.stroke();
        }
        break;

      case 'arrow':
        if (action.start && action.end) {
          this.drawArrow(ctx, action.start, action.end, action.lineWidth);
        }
        break;

      case 'circle':
        if (action.start && action.end) {
          const cx = (action.start.x + action.end.x) / 2;
          const cy = (action.start.y + action.end.y) / 2;
          const rx = Math.abs(action.end.x - action.start.x) / 2;
          const ry = Math.abs(action.end.y - action.start.y) / 2;
          if (rx > 0 && ry > 0) {
            ctx.beginPath();
            ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI);
            ctx.stroke();
          }
        }
        break;

      case 'rect':
        if (action.start && action.end) {
          const w = action.end.x - action.start.x;
          const h = action.end.y - action.start.y;
          if (Math.abs(w) > 1 && Math.abs(h) > 1) {
            ctx.strokeRect(action.start.x, action.start.y, w, h);
          }
        }
        break;

      case 'text':
        if (action.text && action.start) {
          const fs = action.lineWidth * 4 + 14;
          ctx.font = `bold ${fs}px Arial, sans-serif`;
          ctx.shadowColor = 'rgba(0,0,0,0.6)';
          ctx.shadowBlur = 3;
          ctx.fillText(action.text, action.start.x, action.start.y);
          ctx.shadowBlur = 0;
        }
        break;
    }
    ctx.restore();
  }

  private drawArrow(
    ctx: CanvasRenderingContext2D,
    from: { x: number; y: number },
    to: { x: number; y: number },
    lineWidth: number
  ) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (Math.sqrt(dx * dx + dy * dy) < 2) return;
    const angle = Math.atan2(dy, dx);
    const headLen = Math.max(lineWidth * 3, 12);

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(to.x - headLen * Math.cos(angle - Math.PI / 6), to.y - headLen * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(to.x - headLen * Math.cos(angle + Math.PI / 6), to.y - headLen * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  }

  // ── Coordinate conversion ─────────────────────────────────────────────────

  private getCanvasPoint(clientX: number, clientY: number): { x: number; y: number } {
    const c = this.canvasRef.nativeElement;
    const rect = c.getBoundingClientRect();
    return {
      x: (clientX - rect.left) * (c.width / rect.width),
      y: (clientY - rect.top) * (c.height / rect.height)
    };
  }

  // ── Zoom / pan ────────────────────────────────────────────────────────────

  private zoomAtPoint(clientX: number, clientY: number, newZoom: number) {
    const clamped = Math.max(0.25, Math.min(6, newZoom));
    const c = this.canvasRef.nativeElement;
    const rect = c.getBoundingClientRect();

    const canvasPx = (clientX - rect.left) / this.zoomLevel;
    const canvasPy = (clientY - rect.top) / this.zoomLevel;
    const natLeft = rect.left - this.panX;
    const natTop  = rect.top  - this.panY;

    this.panX = clientX - natLeft - canvasPx * clamped;
    this.panY = clientY - natTop  - canvasPy * clamped;
    this.zoomLevel = clamped;
  }

  resetZoom() {
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;
    this.applyTransform();
  }

  get zoomPercent(): string {
    return Math.round(this.zoomLevel * 100) + '%';
  }

  // ── Draw state machine ────────────────────────────────────────────────────

  private beginDraw(pt: { x: number; y: number }) {
    if (this.currentTool === 'text') {
      this.promptForText(pt);
      return;
    }
    this.isDrawing = true;
    this.startPoint = pt;
    this.currentPoints = [pt];
  }

  private moveDraw(pt: { x: number; y: number }) {
    if (!this.isDrawing) return;
    this.currentPoints.push(pt);
    this.redrawAll();
    this.drawAction({
      tool: this.currentTool,
      color: this.currentColor,
      lineWidth: this.currentSize,
      start: this.startPoint,
      end: pt,
      points: this.currentPoints
    });
  }

  private finishDraw(pt: { x: number; y: number }) {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    this.currentPoints.push(pt);
    this.actions.push({
      tool: this.currentTool,
      color: this.currentColor,
      lineWidth: this.currentSize,
      start: { ...this.startPoint },
      end: { ...pt },
      points: [...this.currentPoints]
    });
    this.redrawAll();
  }

  private async promptForText(pt: { x: number; y: number }) {
    const alert = await this.alertController.create({
      header: '添加文字标注',
      inputs: [{ name: 'text', type: 'text', placeholder: '请输入标注内容' }],
      buttons: [
        { text: '取消', role: 'cancel' },
        {
          text: '确定',
          handler: (data) => {
            if (data.text && data.text.trim()) {
              this.zone.run(() => {
                this.actions.push({
                  tool: 'text',
                  color: this.currentColor,
                  lineWidth: this.currentSize,
                  start: { ...pt },
                  text: data.text.trim()
                });
                this.redrawAll();
              });
            }
          }
        }
      ]
    });
    await alert.present();
  }

  // ── Mouse events (desktop) ────────────────────────────────────────────────

  onMouseDown(e: MouseEvent) {
    e.preventDefault();
    this.beginDraw(this.getCanvasPoint(e.clientX, e.clientY));
  }

  onMouseMove(e: MouseEvent) {
    if (!this.isDrawing) return;
    this.moveDraw(this.getCanvasPoint(e.clientX, e.clientY));
  }

  onMouseUp(e: MouseEvent) {
    this.finishDraw(this.getCanvasPoint(e.clientX, e.clientY));
  }

  onMouseLeave(e: MouseEvent) {
    if (this.isDrawing) {
      this.finishDraw(this.getCanvasPoint(e.clientX, e.clientY));
    }
  }

  // ── Toolbar actions ───────────────────────────────────────────────────────

  undo() {
    if (this.actions.length > 0) {
      this.actions.pop();
      this.redrawAll();
    }
  }

  clearAnnotations() {
    this.actions = [];
    this.redrawAll();
  }

  setTool(tool: DrawTool) { this.currentTool = tool; }
  setColor(color: string) { this.currentColor = color; }
  setSize(size: number) { this.currentSize = size; }

  save() {
    this.canvasRef.nativeElement.toBlob(
      (blob) => { this.modalController.dismiss({ blob }); },
      'image/jpeg',
      0.92
    );
  }

  dismiss() {
    this.modalController.dismiss(null);
  }
}
